import type { LumiEditorState } from './types';
import type { LumiEditorAction } from './actions';

export const initialState: LumiEditorState = {
  title: '',
  content: {},
  structure: [],
  ui: {
    loading: {},
  },
};

export default function reducer(
  state: LumiEditorState = initialState,
  action: LumiEditorAction
): LumiEditorState {
  switch (action.type) {
    case 'lumiEditor/RESET':
      return initialState;

    case 'lumiEditor/SET_TITLE':
      return {
        ...state,
        title: action.payload.title,
      };

    case 'lumiEditor/ADD_CONTENT': {
      const { content, index } = action.payload;
      const newStructure = [...state.structure];
      if (index !== undefined && index >= 0 && index <= newStructure.length) {
        newStructure.splice(index, 0, content.id);
      } else {
        newStructure.push(content.id);
      }
      return {
        ...state,
        structure: newStructure,
        content: {
          ...state.content,
          [content.id]: content,
        },
      };
    }

    case 'lumiEditor/UPDATE_CONTENT': {
      const { contentId, updates } = action.payload;
      const existingContent = state.content[contentId];
      if (!existingContent) return state;
      return {
        ...state,
        content: {
          ...state.content,
          [contentId]: {
            ...existingContent,
            ...updates,
          } as typeof existingContent,
        },
      };
    }

    case 'lumiEditor/DELETE_CONTENT': {
      const { contentId } = action.payload;
      const newStructure = state.structure.filter((id) => id !== contentId);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [contentId]: _removed, ...remainingContent } = state.content;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [contentId]: _removedLoading, ...remainingLoading } = state.ui.loading;
      return {
        ...state,
        structure: newStructure,
        content: remainingContent,
        ui: {
          ...state.ui,
          loading: remainingLoading,
        },
      };
    }

    case 'lumiEditor/MOVE_CONTENT': {
      const { contentId, toIndex } = action.payload;
      const currentIndex = state.structure.indexOf(contentId);
      if (currentIndex === -1 || toIndex < 0 || toIndex >= state.structure.length) {
        return state;
      }
      const newStructure = [...state.structure];
      newStructure.splice(currentIndex, 1);
      newStructure.splice(toIndex, 0, contentId);
      return {
        ...state,
        structure: newStructure,
      };
    }

    case 'lumiEditor/IMPORT_STATE': {
      const { state: importedState } = action.payload;
      return importedState;
    }

    case 'lumiEditor/SET_CONTENT_LOADING': {
      const { contentId, loading } = action.payload;
      return {
        ...state,
        ui: {
          ...state.ui,
          loading: {
            ...state.ui.loading,
            [contentId]: loading,
          },
        },
      };
    }

    case 'lumiEditor/SET_SAVING': {
      const { saving } = action.payload;
      return {
        ...state,
        ui: {
          ...state.ui,
          saving,
        },
      };
    }

    case 'lumiEditor/SET_WORKSHEET_ID': {
      const { worksheetId } = action.payload;
      return {
        ...state,
        worksheetId,
      };
    }

    case 'lumiEditor/UPDATE_CONTENT_IDS': {
      const { idMapping } = action.payload;

      // Update structure array with new IDs
      const newStructure = state.structure.map((oldId) => idMapping[oldId] || oldId);

      // Update content dictionary keys with new IDs
      const newContent = Object.entries(state.content).reduce(
        (acc, [oldId, contentItem]) => {
          const newId = idMapping[oldId] || oldId;
          acc[newId] = {
            ...contentItem,
            id: newId,
          };
          return acc;
        },
        {} as typeof state.content
      );

      // Update loading state keys with new IDs
      const newLoading = Object.entries(state.ui.loading).reduce(
        (acc, [oldId, loadingState]) => {
          const newId = idMapping[oldId] || oldId;
          acc[newId] = loadingState;
          return acc;
        },
        {} as typeof state.ui.loading
      );

      return {
        ...state,
        content: newContent,
        structure: newStructure,
        ui: {
          ...state.ui,
          loading: newLoading,
        },
      };
    }

    case 'lumiEditor/SET_TOKEN_LIMIT_ERROR': {
      const { error } = action.payload;
      return {
        ...state,
        ui: {
          ...state.ui,
          tokenLimitError: error,
        },
      };
    }

    case 'lumiEditor/CLEAR_TOKEN_LIMIT_ERROR': {
      return {
        ...state,
        ui: {
          ...state.ui,
          tokenLimitError: null,
        },
      };
    }

    default:
      return state;
  }
}
